import { Typography } from '@navi/web-ui/lib/primitives';
import Card from '@navi/web-ui/lib/primitives/Card/Card';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'src/store';
import { createKey } from 'src/utils/CaseDetail.utils';
import {
  calculatePercentage,
  calculatePercentageProperty,
  formatAmount,
  getSanitizedEnumValue,
  pluralisation
} from 'src/utils/commonUtils';
import styles from './index.module.scss';
import {
  calculateDateDifferenceInDays,
  dateFormat,
  DateFormat,
  getFormatDate
} from 'src/utils/DateHelper';
import GridContainer from '@navi/web-ui/lib/layouts/Grid/GridContainer/GridContainer';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import cx from 'classnames';
import AmountBookmarkIcon from 'src/assets/icons/AmountBookmarkIcon';
import AmountBag from 'src/assets/icons/AmountBag';
import SandClockIcon from 'src/assets/icons/SandClockIcon';
import { useEffect, useState } from 'react';
import { PAYMENT_MODE } from '../../interfaces/RepaymentHistory.type';

const OverviewSummaryBox = () => {
  const { loanId = '', customerId = '' } = useParams();
  const [activeCase, setActiveCase] = useState<{ [key: string]: any }>({});
  const [colorProperty, setColorProperty] = useState({
    className: 'green',
    color: 'var(--green-base)'
  });
  const [remainingDays, setRemainingDays] = useState(0);
  const pageData = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );
  const { currentCasePointer = -1, casesData = [] } = useSelector(
    (state: RootState) => state?.allCases?.casesTable
  );
  const { data: caseDetails } = pageData?.details || {};
  const { outstandingAmount, repaymentHistory, currentDpd, unpaidDays } = caseDetails || {};

  const lastPaidRecord =
    repaymentHistory && repaymentHistory?.length > 1
      ? repaymentHistory.reduce((a, b) =>
          getFormatDate(a.businessDate, DateFormat.ISO_DATE_FORMAT).valueOf() >
          getFormatDate(b.businessDate, DateFormat.ISO_DATE_FORMAT).valueOf()
            ? a
            : b
        )
      : repaymentHistory?.length === 1
      ? repaymentHistory[0]
      : null;

  useEffect(() => {
    if (currentCasePointer > -1 && casesData?.length) {
      const currentCase = casesData[currentCasePointer];
      const today = new Date();
      const expiryDate = currentCase?.agentAllocationExpiry
        ? new Date(currentCase?.agentAllocationExpiry)
        : today;
      const remainingDays = calculateDateDifferenceInDays(today, expiryDate, true);
      const percentage = calculatePercentage(remainingDays);
      const properties = calculatePercentageProperty(percentage);
      setActiveCase(currentCase);
      setRemainingDays(remainingDays);
      setColorProperty(properties);
    }
  }, [currentCasePointer, casesData]);

  const casePrioritisationEnabled = useSelector(
    (state: RootState) => state.common.featureFlags?.casePrioritisationEnabled
  );

  return (
    <GridContainer
      className={cx(styles.overviewSummaryWrapper, { 'mt-[8px]': casePrioritisationEnabled })}
    >
      <GridRow>
        <GridColumn md={4}>
          <Card className={styles.cardWrapper}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%'
              }}
            >
              <Typography
                as="div"
                variant="h2"
                className={cx(styles.heading, styles.headingColorRed)}
              >
                {formatAmount(outstandingAmount?.amount, false)}
              </Typography>
              <Typography as="div" variant="h4" className={styles.subtext}>
                {unpaidDays} days since due date
              </Typography>
              <Typography variant="p4" className={styles.subtext2}>
                Outstanding Amount
              </Typography>

              <div className={styles.iconContainer}>
                <AmountBookmarkIcon opacity="0.2" />
              </div>
            </div>
          </Card>
        </GridColumn>
        <GridColumn md={4}>
          <Card className={styles.cardWrapper}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%'
              }}
            >
              <Typography
                as="div"
                variant="h2"
                className={cx(styles.heading, styles.headingColorBlack)}
              >
                {formatAmount(lastPaidRecord?.amount as number, false)}
              </Typography>
              <Typography as="div" variant="h4" className={styles.subtext}>
                Last paid on{' '}
                {lastPaidRecord?.businessDate
                  ? dateFormat(new Date(lastPaidRecord.businessDate), 'DD MMM, YYYY')
                  : '--'}
              </Typography>
              <Typography variant="p4" className={styles.subtext2}>
                Last paid via {getSanitizedEnumValue(PAYMENT_MODE, lastPaidRecord?.repaymentMode)}
              </Typography>

              <div className={styles.iconContainer}>
                <AmountBag opacity="0.2" />
              </div>
            </div>
          </Card>
        </GridColumn>

        {activeCase?.agentAllocationExpiry && (
          <GridColumn md={4}>
            <Card className={cx(styles.cardWrapper, { [styles.zeroDayCard]: remainingDays <= 0 })}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '100%',
                  paddingTop: '4px'
                }}
              >
                <Typography
                  as="div"
                  variant="h2"
                  className={cx(styles.heading, styles[colorProperty.className])}
                  style={{
                    color: '#ffffff',
                    backgroundColor: colorProperty.color,
                    width: 'fit-content',
                    padding: '4px 8px',
                    borderRadius: 6,
                    display: 'inline',
                    marginTop: '6px'
                  }}
                >
                  {remainingDays > 0
                    ? remainingDays
                    : remainingDays == 0
                    ? 'Last day'
                    : 'Deallocated'}
                </Typography>
                <Typography as="div" variant="h4" className={styles.subtext}>
                  {remainingDays > 0 && `${pluralisation('Day', remainingDays, 's')} Remaining `}
                </Typography>
                <Typography variant="p4" className={styles.subtext2}>
                  De-allocation on{' '}
                  {activeCase?.agentAllocationExpiry && (
                    <span style={{ color: colorProperty.color }}>
                      <strong>
                        {dateFormat(
                          new Date(activeCase?.agentAllocationExpiry),
                          DateFormat.LONG_DATE_FORMAT_WITH_TIME
                        )}
                      </strong>
                    </span>
                  )}
                </Typography>

                <div className={styles.iconContainer}>
                  <SandClockIcon opacity="0.2" fillColor={colorProperty.color} />
                </div>
              </div>
            </Card>
          </GridColumn>
        )}
      </GridRow>
    </GridContainer>
  );
};

export default OverviewSummaryBox;
